import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Text } from '@zillow/constellation-10';

import { useIsOnReviewStep, useMultiplexDetails } from '../../../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../../../hooks/queries/properties';

import PhotoGallery from './components/PhotoGallery';
import PhotoUpload from './components/PhotoUpload';

import type { PhotoProps } from '@zg-rentals/maintenance-utils';

/**
 * Property photos for the create-listing wizard.
 *
 * During the multiplex wizard, photos upload to the multiplex flow, not the creation flow yet.
 * We read photos from multiplex details on wizard steps and from creation flow details on review.
 * Creation flow details do not include photos that only exist on the multiplex flow.
 *
 * We keep local photo state so new uploads show up right away after upload succeeds,
 * before the details query refetches. That is a simple optimistic update: PhotoUpload
 * calls onUploadSuccess, we append to photos, the gallery updates, then multiplex or
 * creation flow details refetch and the useEffect syncs server data back in.
 */
const PhotoUploadFormField = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const isOnReviewStep = useIsOnReviewStep();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { data: multiplexData } = useMultiplexDetails({ enabled: !isOnReviewStep });

  const [photos, setPhotos] = useState<Array<PhotoProps>>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const isUsingMultiplexPhotos = !isOnReviewStep && multiplexData?.propertyPhotos != null;

  /** Load photos from multiplex details on wizard steps. */
  useEffect(() => {
    if (isUsingMultiplexPhotos) {
      setPhotos(multiplexData.propertyPhotos);
    }
  }, [isUsingMultiplexPhotos, multiplexData?.propertyPhotos]);

  /** Load photos from creation flow details on review, or before multiplex details are available. */
  useEffect(() => {
    if (!isUsingMultiplexPhotos) {
      setPhotos(listingDetails?.propertyPhotos ?? []);
    }
  }, [isUsingMultiplexPhotos, listingDetails?.propertyPhotos]);

  /** Add uploaded photos to the gallery right away, before refetch finishes. */
  const handleUploadSuccess = (uploadedPhotos: Array<PhotoProps>) => {
    if (uploadedPhotos.length === 0) {
      return;
    }
    setPhotos((prev) => [...prev, ...uploadedPhotos]);
  };

  const isFeed = listingDetails?.isFeed ?? false;

  if (!photos?.length && isFeed) {
    return (
      <Box css={{ width: '100%' }} data-testid="PhotoUploadFormField">
        <Text css={{ marginTop: '200', flex: '1 0 fit-content' }}>No photos available</Text>
      </Box>
    );
  }

  return (
    <Box css={{ width: '100%' }} data-testid="PhotoUploadFormField">
      {photos.length > 0 || uploadingCount > 0 ? (
        <PhotoGallery
          photoContext={{ type: 'property', listingAlias }}
          photos={photos}
          setPhotos={setPhotos}
          uploadingCount={uploadingCount}
          setUploadingCount={setUploadingCount}
          onUploadSuccess={handleUploadSuccess}
        />
      ) : (
        <>
          {!isFeed && (
            <PhotoUpload
              photoContext={{ type: 'property', listingAlias }}
              setUploadingCount={setUploadingCount}
              uploadingCount={uploadingCount}
              onUploadSuccess={handleUploadSuccess}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default PhotoUploadFormField;
